// Castintech dashboard — Runtime Health view modülü (Faz 28 PR-B).
// Backend: GET /castintech-api/runtime/health (60s cache).
// Görüntüler: issue throughput chart (7g), stuck issue tablosu, agent heatmap,
// auth-fail counter (badge if > 0 son 24h).
//
// Tasarım: D3/Chart.js bağımlılığı YOK — pure SVG + DOM. Bundle boyutu artmaz.
// Render policy: page'e geçişte loadRuntimeHealth() çağrılır; ardından 60s
// otomatik refresh (page açık kaldığı sürece). Page kapanınca timer durur.

import { apiFetch } from '../api.js';
import { escHtml, $ } from '../utils.js';

interface ThroughputBucket {
  date: string;
  total: number;
  done: number;
  in_progress: number;
  blocked: number;
  todo: number;
}

interface StuckIssue {
  id: string | number;
  title: string;
  status: string;
  company_id?: string | null;
  assignee_agent_id?: string | null;
  age_hours: number;
  last_comment_age_hours: number;
}

interface HeatmapCell {
  agent_name: string;
  day_of_week: number;
  hour: number;
  count: number;
}

interface AuthFailSample {
  id: number;
  title: string;
  message: string;
  action: string;
  created_at: string;
}

interface AdapterHealth {
  stuckRunsCleaned: number;
  totalRuns: number;
  claudeTierPct: number;
  routerFallbackCount: number;
  failedCount: number;
}

interface RuntimeHealthPayload {
  generated_at: string;
  elapsed_ms: number;
  issue_count: number;
  issue_throughput_7d: ThroughputBucket[];
  stuck_issues: StuckIssue[];
  agent_heatmap: HeatmapCell[];
  auth_fail_24h: { count: number; samples: AuthFailSample[]; error?: string; skipped?: string };
  adapter_health?: AdapterHealth | null;
  cached?: boolean;
}

let _runtimePayload: RuntimeHealthPayload | null = null;
let _runtimeTimer: ReturnType<typeof setInterval> | null = null;

// Paperclip URL — issue link için (config.ts'te API var ama o /castintech-api/proxy)
const PAPERCLIP_BASE = '/castintech-api/proxy/api';

export async function loadRuntimeHealth(): Promise<void> {
  const sub = $('runtime-health-sub');
  if (sub) sub.textContent = 'telemetri yükleniyor...';
  try {
    const r = await apiFetch('/castintech-api/runtime/health');
    if (!r.ok) throw new Error('runtime/health HTTP ' + r.status);
    _runtimePayload = (await r.json()) as RuntimeHealthPayload;
    renderRuntimeHealth();
  } catch (e) {
    if (sub) sub.textContent = 'hata: ' + (e as Error).message;
  }
}

export function startRuntimeHealthTimer(): void {
  stopRuntimeHealthTimer();
  _runtimeTimer = setInterval(() => {
    void loadRuntimeHealth();
  }, 60_000);
}

export function stopRuntimeHealthTimer(): void {
  if (_runtimeTimer) {
    clearInterval(_runtimeTimer);
    _runtimeTimer = null;
  }
}

export function renderRuntimeHealth(): void {
  const sub = $('runtime-health-sub');
  if (!_runtimePayload) {
    if (sub) sub.textContent = '— veri yok';
    return;
  }
  const p = _runtimePayload;
  const generated = new Date(p.generated_at).toLocaleString('tr-TR');
  if (sub) {
    sub.textContent = `${p.issue_count} issue • ${p.elapsed_ms} ms • ${generated}` +
      (p.cached ? ' (cache)' : '');
  }
  renderAuthFailBadge(p.auth_fail_24h);
  renderAdapterHealth(p.adapter_health || null);
  renderThroughputChart(p.issue_throughput_7d);
  renderStuckTable(p.stuck_issues);
  renderHeatmap(p.agent_heatmap);
}

// Faz 51 P51.3 — Adapter sağlığı panel (claude_local, son 24h).
// 5 metrik kart: totalRuns / claudeTierPct / routerFallback / failed / stuckRunsCleaned.
// claudeTierPct ≥85 → ✅ yeşil, 60-84 → 🟡 sarı, <60 → 🔴 kırmızı.
function renderAdapterHealth(h: AdapterHealth | null): void {
  const root = $('runtime-adapter-health');
  if (!root) return;
  if (!h || h.totalRuns === 0) {
    root.innerHTML = '<div class="rt-empty">Son 24 saatte claude_local run yok.</div>';
    return;
  }
  const pct = h.claudeTierPct || 0;
  const status = pct >= 85 ? 'ok' : pct >= 60 ? 'warn' : 'danger';
  const emoji = pct >= 85 ? '✅' : pct >= 60 ? '🟡' : '🔴';
  const cards = [
    { label: 'Toplam run', value: h.totalRuns, hint: 'son 24h heartbeat_runs (claude_local)' },
    { label: 'Claude tier %', value: `${emoji} ${pct}%`, hint: 'doğrudan Claude direct success oranı', status },
    { label: 'Router fallback', value: h.routerFallbackCount, hint: 'OpenRouter/Codex tarafından kurtarılan' },
    { label: 'Failed', value: h.failedCount, hint: 'status=failed (rate-limit / hata)' },
    { label: 'Stuck cleanup', value: h.stuckRunsCleaned, hint: 'P50.2 cron tarafından temizlenen hayalet run' },
  ];
  root.innerHTML = `<div class="rt-adapter-grid" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));gap:8px;">${cards.map(c => `
    <div class="rt-adapter-card${c.status ? ' rt-' + c.status : ''}" style="padding:10px;border:1px solid #2a3548;border-radius:6px;background:#0f1623;" title="${escHtml(c.hint)}">
      <div style="font-size:11px;color:#9aa5b1;margin-bottom:4px;">${escHtml(c.label)}</div>
      <div style="font-size:18px;font-weight:600;color:#e6edf3;">${escHtml(String(c.value))}</div>
    </div>
  `).join('')}</div>`;
}

function renderAuthFailBadge(authFail: RuntimeHealthPayload['auth_fail_24h']): void {
  const el = $('runtime-authfail-badge');
  if (!el) return;
  const c = authFail?.count || 0;
  if (c <= 0) {
    el.textContent = '0';
    el.className = 'rt-badge ok';
  } else {
    el.textContent = String(c);
    el.className = 'rt-badge danger';
  }
  // Sample list
  const samples = $('runtime-authfail-samples');
  if (samples) {
    if (!authFail?.samples?.length) {
      samples.innerHTML = '<div class="rt-empty">Son 24 saatte auth fail event yok.</div>';
    } else {
      samples.innerHTML = authFail.samples.slice(0, 10).map((s) => {
        const t = new Date(s.created_at).toLocaleString('tr-TR');
        return `<div class="rt-authfail-row">
          <span class="rt-authfail-time">${escHtml(t)}</span>
          <span class="rt-authfail-action">${escHtml(s.action || s.title || '')}</span>
          <span class="rt-authfail-msg">${escHtml(s.message)}</span>
        </div>`;
      }).join('');
    }
  }
}

function renderThroughputChart(buckets: ThroughputBucket[]): void {
  const root = $('runtime-throughput-chart');
  if (!root) return;
  if (!buckets.length) {
    root.innerHTML = '<div class="rt-empty">Veri yok.</div>';
    return;
  }
  const W = 640, H = 200, PADL = 36, PADB = 28, PADT = 12, PADR = 12;
  const maxTotal = Math.max(1, ...buckets.map((b) => b.total));
  const stepX = (W - PADL - PADR) / Math.max(1, buckets.length - 1);

  const points = buckets.map((b, i) => {
    const x = PADL + i * stepX;
    const y = H - PADB - (b.total / maxTotal) * (H - PADT - PADB);
    return { x, y, b };
  });
  const linePath = points.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1].x},${H - PADB} L${points[0].x},${H - PADB} Z`;

  const yTicks = [0, Math.round(maxTotal / 2), maxTotal];
  const yLines = yTicks.map((t) => {
    const y = H - PADB - (t / maxTotal) * (H - PADT - PADB);
    return `<line x1="${PADL}" x2="${W - PADR}" y1="${y}" y2="${y}" stroke="#283047" stroke-dasharray="3,3"/>
            <text x="${PADL - 6}" y="${y + 3}" fill="#9aa5b1" font-size="10" text-anchor="end">${t}</text>`;
  }).join('');

  const xLabels = points.map((p) => {
    const d = p.b.date.slice(5);
    return `<text x="${p.x}" y="${H - 8}" fill="#9aa5b1" font-size="10" text-anchor="middle">${d}</text>`;
  }).join('');

  const dots = points.map((p) =>
    `<circle cx="${p.x}" cy="${p.y}" r="3" fill="#60a5fa">
       <title>${p.b.date}: ${p.b.total} (done=${p.b.done}, blocked=${p.b.blocked})</title>
     </circle>`
  ).join('');

  root.innerHTML = `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" xmlns="http://www.w3.org/2000/svg">
    ${yLines}
    <path d="${areaPath}" fill="rgba(96,165,250,0.16)"/>
    <path d="${linePath}" stroke="#60a5fa" stroke-width="2" fill="none"/>
    ${dots}
    ${xLabels}
  </svg>`;
}

function renderStuckTable(stuck: StuckIssue[]): void {
  const root = $('runtime-stuck-table');
  if (!root) return;
  if (!stuck.length) {
    root.innerHTML = '<div class="rt-empty">Stuck issue yok (in_progress > 2h + last_comment > 4h).</div>';
    return;
  }
  const rows = stuck.map((s) => {
    const link = `${PAPERCLIP_BASE}/issues/${encodeURIComponent(String(s.id))}`;
    return `<tr>
      <td class="rt-mono"><a href="${link}" target="_blank" rel="noopener">${escHtml(String(s.id).slice(0, 8))}</a></td>
      <td>${escHtml(s.title)}</td>
      <td>${escHtml(s.assignee_agent_id || '-')}</td>
      <td class="rt-num">${s.age_hours.toFixed(1)}h</td>
      <td class="rt-num">${s.last_comment_age_hours.toFixed(1)}h</td>
    </tr>`;
  }).join('');
  root.innerHTML = `<table class="rt-table">
    <thead><tr>
      <th>ID</th><th>Başlık</th><th>Atanan</th><th>Yaş</th><th>Son yorum</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>`;
}

function renderHeatmap(cells: HeatmapCell[]): void {
  const root = $('runtime-heatmap');
  if (!root) return;
  if (!cells.length) {
    root.innerHTML = '<div class="rt-empty">Aktivite verisi yok.</div>';
    return;
  }
  // Aggregate by day_of_week × hour (collapse agent dimension to total)
  const grid = new Array(7).fill(null).map(() => new Array(24).fill(0));
  let max = 0;
  for (const c of cells) {
    const d = c.day_of_week;
    const h = c.hour;
    if (d < 0 || d > 6 || h < 0 || h > 23) continue;
    grid[d][h] += c.count;
    if (grid[d][h] > max) max = grid[d][h];
  }
  const days = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
  const cellW = 18, cellH = 18, padL = 28, padT = 18;
  const W = padL + 24 * cellW + 8;
  const H = padT + 7 * cellH + 14;

  let svg = `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" xmlns="http://www.w3.org/2000/svg">`;
  for (let h = 0; h < 24; h += 4) {
    svg += `<text x="${padL + h * cellW + cellW / 2}" y="${padT - 5}" fill="#9aa5b1" font-size="9" text-anchor="middle">${h}</text>`;
  }
  for (let d = 0; d < 7; d++) {
    svg += `<text x="${padL - 4}" y="${padT + d * cellH + cellH / 2 + 3}" fill="#9aa5b1" font-size="10" text-anchor="end">${days[d]}</text>`;
    for (let h = 0; h < 24; h++) {
      const v = grid[d][h];
      const intensity = max > 0 ? v / max : 0;
      const opacity = v === 0 ? 0.05 : 0.18 + 0.82 * intensity;
      svg += `<rect x="${padL + h * cellW + 1}" y="${padT + d * cellH + 1}" width="${cellW - 2}" height="${cellH - 2}" rx="2" fill="rgba(96,165,250,${opacity.toFixed(3)})"><title>${days[d]} ${h}:00 — ${v}</title></rect>`;
    }
  }
  svg += '</svg>';
  root.innerHTML = svg;
}

export function bindRuntimeHealthRefresh(): void {
  const btn = document.getElementById('runtime-health-refresh-btn');
  if (btn) {
    btn.addEventListener('click', () => {
      void loadRuntimeHealth();
    });
  }
}
